"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface TrainerColorsContextType {
  addButtonColor: string;
  cancelButtonColor: string;
  isLoading: boolean;
  isColorSet: boolean;
}

export const TrainerColorsContext = createContext<
  TrainerColorsContextType | undefined
>(undefined);

const DEFAULT_ADD_COLOR = "#0070f3"; // default blue
const DEFAULT_CANCEL_COLOR = "#6b7280"; // gray-500

interface TrainerColorsProviderProps {
  children: ReactNode;
}

export function TrainerColorsProvider({
  children,
}: TrainerColorsProviderProps) {
  const [addButtonColor, setAddButtonColor] =
    useState<string>(DEFAULT_ADD_COLOR);
  const [cancelButtonColor, setCancelButtonColor] =
    useState<string>(DEFAULT_CANCEL_COLOR);
  const [isColorSet, setIsColorSet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load colors from backend on mount
  useEffect(() => {
    const fetchColors = async () => {
      // Only fetch colors if trainer is authenticated
      if (typeof window !== "undefined") {
        const isAuthenticated = localStorage.getItem("isAuthenticated");
        const userRole = localStorage.getItem("userRole");
        if (isAuthenticated !== "true" || userRole !== "trainer") {
          console.log("🎓 Skipping color fetch - trainer not authenticated");
          setIsLoading(false);
          return;
        }
      }

      try {
        setIsLoading(true);

        console.log("🎓 Fetching trainer colors from backend...");

        // Dynamically import to avoid circular dependencies
        const { trainerApi } = await import("@/services/trainer");
        const response = await trainerApi.getTrainerColors();

        console.log("🎓 Trainer colors response:", response);

        if (response.success === 1 && response.data) {
          const { primary_colour, secondary_colour } = response.data;

          console.log("🎓 Fetched colors:", { primary_colour, secondary_colour });

          if (primary_colour || secondary_colour) {
            if (primary_colour) {
              console.log("🎓 Setting primary color:", primary_colour);
              setAddButtonColor(primary_colour);
            }
            if (secondary_colour) {
              console.log("🎓 Setting secondary color:", secondary_colour);
              setCancelButtonColor(secondary_colour);
            }
            setIsColorSet(true);

            // Sync to local storage for persistence
            if (typeof window !== "undefined") {
              if (primary_colour)
                localStorage.setItem("trainer-add-button-color", primary_colour);
              if (secondary_colour)
                localStorage.setItem(
                  "trainer-cancel-button-color",
                  secondary_colour
                );
            }
            return;
          } else {
            console.log("🎓 Both colors are null, using defaults");
          }
        } else {
          console.log(
            "🎓 Backend response unsuccessful or no data:",
            response
          );
        }
      } catch (error) {
        console.error("❌ Failed to fetch trainer owner colors:", error);
      } finally {
        setIsLoading(false);
      }

      // Fallback to localStorage if backend didn't have colors
      console.log("🎓 Falling back to localStorage");
      if (typeof window !== "undefined") {
        const savedAddColor = localStorage.getItem("trainer-add-button-color");
        const savedCancelColor = localStorage.getItem(
          "trainer-cancel-button-color"
        );

        console.log("🎓 localStorage colors:", { savedAddColor, savedCancelColor });

        if (savedAddColor) {
          setAddButtonColor(savedAddColor);
        }
        if (savedCancelColor) {
          setCancelButtonColor(savedCancelColor);
        }
      }
    };

    fetchColors();
  }, []);

  return (
    <TrainerColorsContext.Provider
      value={{
        addButtonColor,
        cancelButtonColor,
        isLoading,
        isColorSet,
      }}
    >
      {children}
    </TrainerColorsContext.Provider>
  );
}

export const useTrainerColors = () => {
  const context = useContext(TrainerColorsContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      addButtonColor: DEFAULT_ADD_COLOR,
      cancelButtonColor: DEFAULT_CANCEL_COLOR,
      isLoading: false,
      isColorSet: false,
    };
  }
  return context;
};
